import { DOCUMENT, isPlatformBrowser, isPlatformServer, LocationStrategy } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  EventEmitter,
  Inject,
  Injectable,
  Optional,
  PLATFORM_ID,
  TransferState,
  inject,
  makeStateKey,
  signal
} from '@angular/core';
import { Params, ActivatedRoute } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import {
  BehaviorSubject,
  combineLatest,
  first,
  firstValueFrom,
  from,
  Observable,
  of,
  switchMap,
  take,
  tap
} from 'rxjs';

import { ENVIRONMENT, Environment, isDemo, isDevelop } from '@environment';
import { CountryService } from '@usheru-hff/sdk/country';
import { isLocalhost } from '@usheru-hff/sdk/utils';
import { UsheruApi } from '@usheru-hff/shared/data-access-backend';
import { Channel, Country, LocationApi, Location, User } from '@usheru-hff/shared/domain';
import { GoogleMapsLoaderService } from '@usheru-hff/shared/feature-location-selector';
import { COUNTRY_VERSION_TOKEN, VIEWER_COUNTRY_TOKEN } from '@usheru-hff/shared/tokens';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';

import { GeoLocationService } from './geo-location.service';
import { getLocaleFromUrl } from './location.utils';
import { GeoLocationComplete } from './model/geoLocation';
import {
  defaultChannelLocationStateKey,
  userLocationStateKey,
  ushCountryVersionKey,
  VIEWER_COUNTRY_KEY
} from './stateKey-location';

export const LOCATION_KEY = 'location';
const LOCATION_TRANSFER_KEY = makeStateKey<Location>('LOCATION_DATA_KEY');

export const SELECTED_COUNTRY_KEY = 'selectedCountry';
const SELECTED_COUNTRY_TRANSFER_KEY = makeStateKey<Country>('SELECTED_COUNTRY_DATA_KEY');

@Injectable({
  providedIn: 'root'
})
export class LocationService {
  private readonly transferState = inject(TransferState);
  private readonly googleMapsLoader = inject(GoogleMapsLoaderService);
  private readonly countryService = inject(CountryService);
  private readonly geoLocationService = inject(GeoLocationService);
  private readonly cookieService = inject(CookieService);
  private readonly route = inject(ActivatedRoute);
  private readonly locationStrategy = inject(LocationStrategy);
  private readonly document = inject(DOCUMENT);

  /**
   * COUNTRY_VERSION_TOKEN is setup on `proxy-server` during SSR.
   **/
  private readonly countryVersionFromToken = inject(COUNTRY_VERSION_TOKEN, { optional: true });
  private readonly viewerCountryFromToken = inject(VIEWER_COUNTRY_TOKEN, { optional: true });
  private readonly countryVersionFromCookie = this.cookieService.get(ushCountryVersionKey);

  authLocation = signal<Location | undefined>(undefined);
  authCountry = signal<Country | undefined>(undefined);

  /**
   * ⬆️⬆️⬆️ not used. We use transferState instead
   *
  /* userCountryCode and _userCountry will be generated by cloudfront-viewer-header*/

  private userCountryCode: string;
  private _userCountry: Country;

  /*
    _selectedLocation will be only set if user picks a location from a dropdown, this will also be
    saved to local storage for future reference
  */
  private _selectedCountry: Country;
  private _channelCountry: Country;
  private _channelLocation: Location;

  // PART OF Collection variables

  private _countries: Country[] = [];

  private availableCountries: Country[] = [];

  private readonly isBrowser: boolean;

  $locationChanged = new BehaviorSubject<Location>(null);

  $countryChanged = new EventEmitter<Country>();

  constructor(
    private translationsService: TranslationsService,
    @Inject(ENVIRONMENT) private env: Environment,
    private http: HttpClient,
    private usheruApi: UsheruApi,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    if (this.isBrowser && localStorage.getItem(LOCATION_KEY)) {
      const localLocation = JSON.parse(localStorage.getItem(LOCATION_KEY));
      this.transferState.set(userLocationStateKey, localLocation);
      this.transferState.set(LOCATION_TRANSFER_KEY, localLocation);
    }

    this.loadViewerCountryCode();
  }

  private loadViewerCountryCode() {
    if (this.isBrowser) {
      this.userCountryCode = this.transferState.get<string>(VIEWER_COUNTRY_KEY, '');
    } else {
      this.transferState.set<string>(VIEWER_COUNTRY_KEY, this.viewerCountryFromToken);
    }
  }

  cleanLocationStorage() {
    localStorage.removeItem(LOCATION_KEY);
    this.transferState.remove(LOCATION_TRANSFER_KEY);
  }

  init(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      combineLatest({
        allCountries: this.countryService.getAllCountries()
      }).subscribe(
        data => {
          this._countries = <Country[]>data.allCountries;
          resolve();
        },
        err => {
          console.error(err);
          reject(err);
        }
      );
    });
  }

  get userLocation(): Location {
    const stateLocation = this.transferState.get<Location>(userLocationStateKey, null);
    if (stateLocation) return stateLocation;

    if (this.isBrowser) {
      const stored = localStorage.getItem(LOCATION_KEY);
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch (e) {
          console.warn('Invalid location in localStorage');
        }
      }
    }

    return this.defaultChannelLocation;
  }

  set userLocation(location: Location) {
    this.transferState.set(userLocationStateKey, location);
    /* this.cookieService.set(userLocationStateKey, JSON.stringify(location)); */
  }

  get defaultChannelLocation(): Location {
    return this.transferState.get<Location>(defaultChannelLocationStateKey, null);
  }

  get localStorageSelectedCountry(): Country | null {
    // 1. Prefer TransferState during SSR/browser hydration
    const stateCountry = this.transferState.get(SELECTED_COUNTRY_TRANSFER_KEY, null);
    if (stateCountry) return stateCountry;

    // 2. Fall back to localStorage
    if (this.isBrowser) {
      const raw = localStorage.getItem(SELECTED_COUNTRY_KEY);
      if (raw) {
        try {
          return JSON.parse(raw);
        } catch (e) {
          console.warn('Invalid selectedCountry in localStorage');
        }
      }
    }

    return null;
  }

  // try to return userCountryName || from headers | null
  // DO NOT MODIFY THIS FUNCTION OR SUBFUNCTIONS
  get userCountry() {
    if (this._userCountry) return this._userCountry;

    if (this.userCountryCode) {
      this._userCountry = this.getCountryByShortCode(this.userCountryCode); // TODO: We might consider to not retrieve countries for most of the cases
      return this._userCountry;
    }

    return null;
  }

  get channelCountry() {
    // Channel country is almost never user, the united kindom default country is for when
    // we want to test the uplayer, it always needs an aws country header, so becouse in localhost
    // we dont have aws country header, we use the channel Country if there is any o we defaulted to united kingdom
    return this._channelCountry || this.getAvailableCountries()[0];
  }

  /**
   * Will return the real aws header country if it is prod environment
   * else will return the selected country
   */

  get uplayerCountry() {
    if (isDevelop(this.env) || isDemo(this.env) || isLocalhost(this.document)) {
      return this.selectedCountry;
    } else {
      return this.userCountry;
    }
  }

  get selectedCountry(): Country {
    // if exists return it.
    if (this._selectedCountry) {
      return this._selectedCountry;
    }

    const countryFromSSRToken =
      this.countryVersionFromToken && this.getCountryByShortCode(this.countryVersionFromToken);

    if (countryFromSSRToken) {
      this._selectedCountry = countryFromSSRToken;
      this.storeSelectedCountry(this._selectedCountry);
    }

    const countryFromCookie =
      this.countryVersionFromCookie && this.getCountryByShortCode(this.countryVersionFromCookie);

    if (countryFromCookie) {
      this._selectedCountry = countryFromCookie;
      this.storeSelectedCountry(this._selectedCountry);
    }

    // if selectedCountry localStorage exists return it
    if (this.localStorageSelectedCountry) {
      this._selectedCountry = this.localStorageSelectedCountry;
      return this._selectedCountry;
    }

    // if no selectedCountry from localStorage grab de one from aws header (real country)
    if (this.userCountry) {
      return this.userCountry;
    }

    // in last instance try to get the country from channel if exists

    if (this.channelCountry) {
      return this.channelCountry;
    }
    return null;
  }

  set selectedCountry(country: Country) {
    this.storeSelectedCountry(country);
    this._selectedCountry = country;
    this.$countryChanged.next(country);
  }

  get channelLocation() {
    if (this._channelLocation) return this._channelLocation;
    return null;
  }

  set channelLocation(location: Location) {
    if (!location) return;
    location.address = location?.address || this.translationsService.translate('public.filters.location.placeholder');
    this._channelLocation = location;
    this._channelCountry = this.getCountryByName(this.getCountryNameByAddress(location.address));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  getCountryByAddress = (address: any): Country => {
    const countryName = this.getCountryNameByAddress(address);
    return this.findCountryByName(countryName);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getCountryNameByAddress(address: any): string {
    if (address?.address_components != null) {
      const component = address.address_components.find(
        comp => comp.types.includes('country') && comp.types.includes('political')
      );

      if (component != null) {
        return component.long_name;
      }
    }

    return null;
  }

  // this function will return a Country object given a 2letters country code e.g. 'GB' code for Great Britain

  getCountryByShortCode(shortCode: string | undefined): Country | undefined {
    return this._countries.find(c => c.code2?.toLowerCase() === shortCode?.toLowerCase());
  }

  getCountryByName(countryName: string): Country {
    // If it doesn't find the country it means that the country list was being restricted for the channel,
    // in that case then use the first country of the country list retrieved from backend.
    if (countryName) {
      const country = this._countries.find(c => c.name?.toLowerCase() === countryName.toLowerCase());
      return country || this._countries[0];
    }
    return this._countries[0];
  }

  getAvailableCountrySelected(availableCountries: Country[]): Country {
    const authCountry = this.authCountry?.() ?? null;
    let streamingCountry: Country;

    streamingCountry = availableCountries.find(country => this.selectedCountry.id == country.id);

    // if streamingCountry availables for channel could not be matched with selected country grab first country available
    if (!streamingCountry) {
      streamingCountry = authCountry ?? this.selectedCountry ?? this.channelCountry;

      this.selectedCountry = streamingCountry;
    }
    return streamingCountry;
  }

  getCountryFromAvailableShortCode(shortCode: string) {
    return this.availableCountries.find(c => c.code2?.toLowerCase() === shortCode?.toLowerCase());
  }

  // setUserLocation refactor
  setUserLocation(location: Location) {
    this.storeLocation(location);
    this.userLocation = location;
    this.$locationChanged.next(location);
    // this.transferState.set(makeStateKey<Location>('userLocation'), location);
  }

  async setUserLocationFormCords(lat: number, long: number, updateUserLocation = true): Promise<Location> {
    if (!lat || !long) return null;

    if (isLocalhost(this.document)) return null;
    if (!this.googleMapsLoader.$mapsLoaded.value)
      // making sure that the google maps scripts are loaded before trying to use them
      this.googleMapsLoader.loadGoogleMapsScripts();

    return new Promise<Location>(resolve => {
      this.googleMapsLoader.$mapsLoaded.pipe(first(val => val)).subscribe(() => {
        const latlng = new google.maps.LatLng(lat, long);

        const geocoder = new google.maps.Geocoder();
        geocoder?.geocode({ location: latlng }, (results, status) => {
          if (status === 'OK') {
            results = results.sort((a, b) => {
              const aLocationType = a.geometry.location_type;
              const bLocationType = b.geometry.location_type;
              const aTypes = a.types;
              const bTypes = b.types;
              const compareLocationType = (lta: string, ltb: string) => {
                if (lta === 'APROXIMATE') return 1;
                if (ltb === 'APROXIMATE') return -1;
                if (lta === 'GEOMETRIC_CENTER') return 1;
                if (ltb === 'GEOMETRIC_CENTER') return -1;
                if (lta === 'RANGE_INTERPOLATED') return 1;
                if (ltb === 'RANGE_INTERPOLATED') return -1;
                if (lta === 'ROOFTOP') return 1;
                if (ltb === 'ROOFTOP') return -1;
                return 0;
              };

              const compareTypes = (aTypes: string[], bTypes: string[]) => {
                // Prioritize the results 'administrative_area_level'
                const aType = aTypes.find(type => type.includes('administrative_area_level'));
                const bType = bTypes.find(type => type.includes('administrative_area_level'));

                if (aType && bType) {
                  return bType.localeCompare(aType);
                }
                if (aType && !bType) return -1;
                if (!aType && bType) return 1;
                return 0;
              };

              const locationTypeComp = compareLocationType(aLocationType, bLocationType);
              if (locationTypeComp !== 0) return locationTypeComp;
              const typesComp = compareTypes(aTypes, bTypes);
              if (typesComp !== 0) return typesComp;
              return 0;
            });

            const address = results[0].formatted_address;
            const location = { latitude: lat, longitude: long, address };
            if (updateUserLocation) this.setUserLocation(location);
            resolve(location);
          }
        });
      });
    });
  }

  processUserLocation(user: User, updateUserLocation = true): Observable<boolean> {
    const { latitude, longitude, address } = this.userLocation || {};

    // Check if the user has a location is already set

    if (address && latitude === user?.latitude && longitude === user?.longitude) {
      this.authLocation.set(this.userLocation);

      return of(true);
    } else {
      return from(this.setUserLocationFormCords(user.latitude, user.longitude, updateUserLocation)).pipe(
        take(1),
        tap(location => this.authLocation.set(location)),
        switchMap(() => of(true))
      );
    }
  }

  processUserCountry(user: User, countries: Country[]): Observable<boolean> {
    if (!user.country) return of(true);

    const updateUserSelectedCountry = true;
    const userCountry = countries.find(c => c.code2 === user.country);
    const selectedCountry = this.selectedCountry;
    if (updateUserSelectedCountry && selectedCountry?.code2 !== userCountry?.code2) {
      this.selectedCountry = userCountry;
    }
    this.authCountry.set(selectedCountry);
    return of(true);
  }

  // This function will find the current user location when button find my location was pressed

  async findMyLocation() {
    let userLoc: Location;

    if ('geolocation' in navigator) {
      userLoc = await this.geoLocFindUserLocation();
      this.setUserLocation(userLoc);
      return this.userLocation;
    } else if (this.userLocation != null) {
      return this.userLocation;
    } else {
      return null;
    }
  }

  private async geoLocFindUserLocation(): Promise<Location> {
    const position = await this.getCurrentPosition();
    const city = await this.getLocationApi(position.coords.latitude, position.coords.longitude).toPromise();
    let address;
    if (city) {
      address = `${city.name}, ${this.translationsService.translate((city.region.country as Country).trMsg)}`;
    } else {
      address = this.translationsService.translate('public.filters.location.theatersAroundYou');
    }

    return {
      address: address,
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      userLoc: true
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  private getCurrentPosition = (): Promise<any> =>
    new Promise(resolve => {
      navigator.geolocation.getCurrentPosition(position => {
        resolve(position);
      });
    });

  findCountryByName(country: string): Country {
    return this._countries.find(c => c.code2?.toLowerCase() === country.toLowerCase());
  }

  private storeLocation(location: Location) {
    this.transferState.set(LOCATION_TRANSFER_KEY, location);

    if (this.isBrowser) {
      localStorage.setItem(LOCATION_KEY, JSON.stringify(location));
    }
  }

  private storeSelectedCountry(country: Country) {
    this.transferState.set(SELECTED_COUNTRY_TRANSFER_KEY, country);

    if (this.isBrowser) {
      localStorage.setItem(SELECTED_COUNTRY_KEY, JSON.stringify(country));
    }
  }
  public getAvailableCountries(): Country[] {
    return this.availableCountries;
  }

  // FUNCTIONS

  //// country

  getLocationApi(lat: number, long: number): Observable<LocationApi> {
    return this.http.get<LocationApi>(`${this.env.api.url}/location/?latitude=${lat}&longitude=${long}`, {
      headers: this.usheruApi.getStandardHeader()
    });
  }

  // API QUERIES

  public setLocationApi(location: Location) {
    const { latitude, longitude } = location;

    this.getLocationApi(latitude, longitude).subscribe(city => {
      if (city) {
        this.selectedCountry = city.region.country as Country;

        this.setUserLocation({
          address: `${city.name}, ${
            this.translationsService.CACHES
              ? this.translationsService.translate((city.region.country as Country).trMsg)
              : this.selectedCountry.name
          }`,

          longitude,

          latitude
        });
      }
    });
  }

  public loadInitialLocation(channel: Channel) {
    const params: Params = this.route.snapshot.params;
    const lat = params['lat'] ? parseFloat(params['lat']) : null;
    const lon = params['lon'] ? parseFloat(params['lon']) : null;
    const uLocation = this.userLocation;

    this.availableCountries = channel.partner?.locationRestrictions?.length
      ? channel.partner?.locationRestrictions
      : this._countries;

    if (uLocation) {
      if (lat && lon && !(lat == uLocation?.latitude && lon == uLocation?.longitude)) {
        return this.setLocationApi({ latitude: lat, longitude: lon });
      } else if (!this.selectedCountry) {
        return this.setLocationApi(uLocation);
      }
    }

    if (!uLocation) {
      firstValueFrom(this.geoLocationService.getCurrentLocation()).then(geoLocation => {
        const country = this.getCountryFromAvailableShortCode(geoLocation.code);

        // If country is on the list of allowed countries
        if (country) {
          return this.setInitialLocationFromGeoLocation({ location: geoLocation, country });
        }
        // or else if it has a default location
        const defaultLocation = this.getDefaultLocation(channel);
        if (defaultLocation) {
          this.setLocationApi(defaultLocation);
        }
      });
    }
  }

  getCoordsFromPointLocation(pointLocation: string): Location | undefined {
    if (pointLocation) {
      const coords = pointLocation
        .replace('POINT (', '')
        .replace(')', '')
        .split(' ')
        .map(c => parseFloat(c));

      return {
        latitude: coords[0],
        longitude: coords[1]
      };
    } else {
      return undefined;
    }
  }

  private getDefaultLocation(channel: Channel): Location | undefined {
    // Default Location from Channel
    let defaultLocation = channel.defaultLocation;

    // If application has `countryBasedApplication` like Scanbox
    if (this.env.countryBasedApplication?.enabled) {
      // Take the language code from URL.
      const localeCode = getLocaleFromUrl(this.locationStrategy);

      // console.info(`1- Country version from cookie: ${this.countryVersionFromCookie}`);
      // console.info(`2- Country version from token: ${this.countryVersionFromToken}`);
      // console.info(`3- Locale code from URL: ${localeCode}`);
      const countryCode = (this.countryVersionFromCookie || this.countryVersionFromToken) ?? localeCode;

      const defaultLocations = this.env.countryBasedApplication?.defaultLocations;
      // Use the default locations from `countryBasedApplication.defaultLocations`
      const currentCountryLocation = countryCode && defaultLocations?.[countryCode];
      // Get the default location from `countryBasedApplication.default`
      const defaultCountryLocation = defaultLocations?.[this.env.countryBasedApplication?.default.languageCode];

      defaultLocation = currentCountryLocation || defaultCountryLocation;
    }

    return this.getCoordsFromPointLocation(defaultLocation);
  }

  private setInitialLocationFromGeoLocation(locationComplete: GeoLocationComplete) {
    const city = locationComplete.location?.city;
    const latitude = locationComplete.location?.latitude;
    const longitude = locationComplete.location?.longitude;

    if (locationComplete.country) {
      this.selectedCountry = locationComplete.country;

      this.setUserLocation({
        address: `${city}, ${
          this.translationsService.CACHES
            ? this.translationsService.translate(locationComplete.country.trMsg)
            : this.selectedCountry.name
        }`,

        longitude,
        latitude
      });
    }
  }
}
